import { computed, inject, Injectable, signal } from '@angular/core';
import { concatMap, finalize, tap } from 'rxjs';

import {
  CustomResultOfListOfNotificationsListDto,
  NotificationImportanceLevel,
  NotificationsListDto,
  NotificationTypes,
} from '../api/models';
import { NotificationsService } from '../api/services';
import { UserSessionService } from './authentication/user-session.service';

@Injectable({
  providedIn: 'root',
})
export class AppNotificationService {
  notificationService = inject(NotificationsService);
  userSession = inject(UserSessionService);
  #filteredImportanceLevel = signal<NotificationImportanceLevel[]>([]);
  #filteredNotificationTypes = signal<NotificationTypes[]>([]);
  filteredImportanceLevel = computed(() => this.#filteredImportanceLevel());
  filteredNotificationTypes = computed(() => this.#filteredNotificationTypes());
  #searchText = signal<string>('');
  initialLoading = signal<boolean>(false);
  loading = signal<boolean>(false);
  notificationTimerValue = signal<number>(0);

  #allNotifications = signal<NotificationsListDto[]>([]);
  allNotifications = computed(() =>
    this.#allNotifications().filter(
      (res) =>
        this.#filteredImportanceLevel()?.includes(res.importanceLevel) &&
        this.#filteredNotificationTypes()?.includes(res.notificationTypes) &&
        res.title?.includes(this.#searchText())
    )
  );
  #unreadNotifications = signal<NotificationsListDto[]>([]);
  unreadNotifications = computed(() =>
    this.#unreadNotifications().filter(
      (res) =>
        this.#filteredImportanceLevel()?.includes(res.importanceLevel) &&
        this.#filteredNotificationTypes()?.includes(res.notificationTypes) &&
        res.title?.includes(this.#searchText())
    )
  );

  // unreadNotifications = computed(this.#notifications);
  allNotificationsCount = computed(() => this.#allNotifications()?.length);
  unreadNotificationsCount = computed(
    () => this.#unreadNotifications()?.length
  );

  setFilteredImportanceLevel(values: NotificationImportanceLevel[]) {
    this.#filteredImportanceLevel.set(Object.assign([], values));
  }
  setFilteredNotificationTypes(values: NotificationTypes[]) {
    this.#filteredNotificationTypes.set(Object.assign([], values));
  }
  onChangeSearchText(text: string) {
    this.#searchText.set(text);
  }

  setNewNotifications(notification: NotificationsListDto) {
    this.#unreadNotifications.update((values) => {
      return [...values, notification];
    });
    this.#allNotifications.update((values) => {
      return [...values, notification];
    });
  }

  getNotificationsApi() {
    this.userSession.isFetchingNotifications = true;
    this.loading.set(true);
    return this.notificationService
      .notificationsGetNotificationsList({
        justNotSeenNotifications: false,
      })
      .pipe(
        concatMap((result: CustomResultOfListOfNotificationsListDto) => {
          if (result.data) this.#allNotifications.set(result.data);
          else this.#allNotifications.set([]);
          return this.notificationService
            .notificationsGetNotificationsList({
              justNotSeenNotifications: true,
            })
            .pipe(
              tap((result: CustomResultOfListOfNotificationsListDto) => {
                if (result.data) this.#unreadNotifications.set(result.data);
                else this.#unreadNotifications.set([]);
              })
            );
        }),
        finalize(() => {
          this.loading.set(false);
          this.userSession.isFetchingNotifications = false;
        })
      );
  }
}
